import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { GripVertical, Maximize2, Minimize2, EyeOff } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { DashColorsProvider, WidgetIdCtx } from './TagDashColors';

// Shared dashboard layout (widgets, hidden, order, widths). seed(): starting layout until the first save.
export function useDashConfig(url, seed) {
  const [cfg, setCfg] = useState({ widgets: [], hidden: [], order: [], widths: {}, colors: {} });
  const norm = (d) => (d.saved || !seed ? { widths: {}, colors: {}, brands: {}, ...d } : { colors: {}, brands: {}, ...seed(), saved: false });
  useEffect(() => { notesApi.get(url).then(({ data }) => setCfg(norm(data))).catch(() => {}); }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  // Re-read the shared layout before each change so two super admins never overwrite each other.
  const queue = useRef(Promise.resolve());
  // Changes run one after another so quick edits never overwrite each other.
  const save = (change) => {
    queue.current = queue.current.then(async () => {
      try {
        const { data } = await notesApi.get(url);
        const next = change(norm(data));
        setCfg(next);
        await notesApi.put(url, next);
      } catch (e) { toast.error(extractError(e, 'Could not save the dashboard.')); }
    });
    return queue.current;
  };
  return [cfg, save];
}

// Ordered ids of every widget (built-in + custom) in the saved order.
export const orderedIds = (builtinIds, c) => {
  const list = [...builtinIds, ...c.widgets.map((x) => x.id)];
  const pos = (id) => { const i = c.order.indexOf(id); return i < 0 ? 1000 + list.indexOf(id) : i; };
  return list.sort((x, y) => pos(x) - pos(y));
};
// Save one legend colour into the shared layout.
export const colorChange = (save) => (batch) => save((c) => {
  const colors = { ...(c.colors || {}) };
  Object.entries(batch).forEach(([wid, m]) => { colors[wid] = { ...(colors[wid] || {}), ...m }; });
  return { ...c, colors };
});
export const moveTo = (ids, from, to) => { const n = ids.filter((x) => x !== from); n.splice(n.indexOf(to) + (ids.indexOf(from) < ids.indexOf(to) ? 1 : 0), 0, from); return n; };

const TB = 'grid h-7 w-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground';

// Drag-and-drop widget grid. Super admins drag the grip to move a widget, and can switch half / full width or hide it.
export function TagDashGrid({ widgets, widthOf, render, onMove, onWidth, onHide, colors, onColor, readOnly = false, brands = [], brandOf = () => '', onBrand }) {
  const [armed, setArmed] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const end = () => { setArmed(null); setDrag(null); setOver(null); };
  return (
    <DashColorsProvider colors={colors} onSave={onColor}>
    <div className="grid gap-5 xl:grid-flow-row-dense xl:grid-cols-2" data-testid="tagws-dash-grid">
      {widgets.map((w) => {
        const full = widthOf(w) === 'full';
        return (
          <div key={w.id} draggable={armed === w.id} onDragStart={(e) => { setDrag(w.id); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={end}
            onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(w.id); } }} onDrop={(e) => { e.preventDefault(); if (drag && drag !== w.id) onMove(drag, w.id); end(); }}
            className={`group relative rounded-3xl transition-[transform,opacity,box-shadow] duration-200 ${full ? 'xl:col-span-2' : ''} ${onBrand && w.id === 'kpis' ? 'pt-6' : ''} ${drag === w.id ? 'scale-[0.98] opacity-40' : ''} ${over === w.id && drag && drag !== w.id ? 'ring-2 ring-emerald-500 ring-offset-4 ring-offset-background' : ''}`} data-testid={`tagws-dash-cell-${w.id}`}>
            {readOnly ? null : <div className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-0.5 rounded-xl border border-border bg-card/95 p-0.5 opacity-0 shadow-lg backdrop-blur transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100" data-export-ignore="true">
              <button type="button" onMouseDown={() => setArmed(w.id)} onMouseUp={() => setArmed(null)} onTouchStart={() => setArmed(w.id)} title="Drag to move" aria-label="Drag to move" className={`${TB} cursor-grab active:cursor-grabbing`} data-testid={`tagws-dash-drag-${w.id}`}><GripVertical size={14} /></button>
              <button type="button" onClick={() => onWidth(w.id, full ? 'half' : 'full')} title={full ? 'Half width' : 'Full width'} aria-label={full ? 'Half width' : 'Full width'} className={TB} data-testid={`tagws-dash-width-${w.id}`}>{full ? <Minimize2 size={13} /> : <Maximize2 size={13} />}</button>
              <button type="button" onClick={() => onHide(w.id)} title="Hide widget" aria-label="Hide widget" className={TB} data-testid={`tagws-dash-hide-${w.id}`}><EyeOff size={13} /></button>
            </div>}
            {onBrand ? (
              <select value={brandOf(w.id)} onChange={(e) => onBrand(w.id, e.target.value)} title="Brand shown in this widget" className={`absolute -top-3.5 left-5 z-[6] h-7 max-w-[150px] rounded-full border px-2 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold shadow-sm ${brandOf(w.id) ? 'border-indigo-500/50 bg-indigo-50 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200' : 'border-border bg-background text-muted-foreground'}`} data-testid={`tagws-dash-brand-${w.id}`}>
                <option value="">All brands</option>
                {[...new Set([...brands, brandOf(w.id)].filter(Boolean))].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            ) : brandOf(w.id) ? <span className="absolute -top-4 left-5 z-[5] rounded-full border border-indigo-500/40 bg-indigo-50 px-2 py-0.5 text-[length:calc(var(--fs,1)*0.65625rem)] font-semibold text-indigo-800 shadow-sm dark:bg-indigo-950 dark:text-indigo-200" data-testid={`tagws-dash-brand-badge-${w.id}`}>{brandOf(w.id)}</span> : null}
            <WidgetIdCtx.Provider value={w.id}>{render(w)}</WidgetIdCtx.Provider>
          </div>
        );
      })}
    </div>
    </DashColorsProvider>
  );
}
